import React from 'react';
import { Button } from '@etherisc/ui-kit';
import { PlusIcon } from 'lucide-react';
import type { ButtonProps } from '@etherisc/ui-kit';

interface PositiveButtonProps extends Omit<ButtonProps, 'variant' | 'children'> {
  children: React.ReactNode;
  showIcon?: boolean;
  iconSize?: number;
  fullWidth?: boolean;
  wide?: boolean;
  style?: React.CSSProperties;
  loadingText?: string;
}

/**
 * Semantic button component for Positive/Constructive actions
 * Uses GREEN color following UX guidelines for safe, beneficial actions
 * Examples: Create, Accept, Approve, Add
 * Includes optional plus icon for create actions
 * Uses @etherisc/ui-kit Button with variant="secondary" and custom success colors
 */
export function PositiveButton(props: PositiveButtonProps) {
  const { 
    children, 
    showIcon = true, 
    iconSize = 16,
    fullWidth = false,
    wide = false,
    className = '',
    style = {},
    loadingText, // eslint-disable-line @typescript-eslint/no-unused-vars
    ...buttonProps 
  } = props;
  // Handle sizing classes - adapt to ui-kit if needed
  const sizeClasses = fullWidth ? 'w-full max-w-[500px]' : wide ? 'w-48' : '';
  const combinedClassName = `flex items-center gap-2 ${sizeClasses} ${className}`.trim();
  
  // Override CSS custom properties for success colors
  const buttonStyle = {
    '--secondary': 'var(--success)',
    '--secondary-foreground': 'var(--success-foreground)',
    ...style
  } as React.CSSProperties;
  
  return (
    <Button 
      {...buttonProps}
      variant="secondary"
      className={combinedClassName}
      style={buttonStyle}
    >
      {showIcon && <PlusIcon size={iconSize} />}
      {children}
    </Button>
  );
}

 